import React from "react";

import { useSelector } from "react-redux";

import { TypeEnum } from "@/src/api/ProjectAPI.types";

import EduOfferConfiguration from "./EduOfferConfiguration";
import { OFFER_PLAN_TYPES } from "./offerPlan.constants";
import OfferPlanTable from "./OfferPlanTable";

import type { Dispatch, JSX, SetStateAction } from "react";

import type { AppState } from "@/src/reducers";

import type { OfferFormState } from "./offerAdapter";
import type { FlatOfferPlan } from "./offerPlanAdapter";
import type { OfferPlanTypeValue } from "../../models/OfferPlan.types";
import type { OfferPlanType } from "../../types/offers";

interface OfferPlansProps {
  offer: OfferFormState;
  setOffer: Dispatch<SetStateAction<OfferFormState>>;
}

const OFFER_PLAN_TYPE_LIST = Object.values(OFFER_PLAN_TYPES);

function getPlansOfType(
  offer: OfferFormState,
  planType: OfferPlanTypeValue,
): OfferPlanType[] {
  return offer.offer_plans.filter((offerPlan) => offerPlan.type === planType);
}

function OfferPlans({ offer, setOffer }: OfferPlansProps): JSX.Element {
  const isEduProject = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.type === TypeEnum.Edu,
  );

  const updateOfferPlans = (
    getOfferPlans: (
      offerPlans: OfferFormState["offer_plans"],
    ) => OfferFormState["offer_plans"],
  ) => {
    setOffer((prevOffer) => ({
      ...prevOffer,
      offer_plans: getOfferPlans(prevOffer.offer_plans),
    }));
  };

  const handleAddPlan = (newPlan: FlatOfferPlan) => {
    updateOfferPlans((offerPlans) => [{ ...newPlan }, ...offerPlans]);
  };

  const handleChangePlan = (updatedPlan: FlatOfferPlan) => {
    updateOfferPlans((offerPlans) =>
      offerPlans.map((offerPlan) =>
        offerPlan.uuid === updatedPlan.uuid ? { ...updatedPlan } : offerPlan,
      ),
    );
  };

  const handleDeletePlan = (planId: string) => {
    updateOfferPlans((offerPlans) =>
      offerPlans.filter((offerPlan) => offerPlan.uuid !== planId),
    );
  };

  if (isEduProject) {
    return (
      <EduOfferConfiguration
        onPlanAdd={handleAddPlan}
        onPlanChange={handleChangePlan}
        plan={offer.offer_plans[0]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-10">
      {OFFER_PLAN_TYPE_LIST.map((planType) => (
        <OfferPlanTable
          key={planType}
          onPlanAdd={handleAddPlan}
          onPlanChange={handleChangePlan}
          onPlanDelete={handleDeletePlan}
          plans={getPlansOfType(offer, planType)}
          planType={planType}
        />
      ))}
    </div>
  );
}

export default OfferPlans;
